var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import '../icon/Icon.js';
import '../button/SimpleButton.js';
/**
 * Master-detail layout: a left rail listing the categories as `<simple-button>`s
 * and a right pane that shows the active category's title plus its content slot.
 *
 * Content is provided through **named slots matching each category id** — only
 * the active category's slot is shown, the others stay in the DOM (so consumer
 * state is preserved across switches).
 *
 * @fires category-change - `{ id }` when the active category changes.
 *
 * @slot <category-id> - One per category; shown only when that category is active.
 */
let NavRailLayout = class NavRailLayout extends LitElement {
    constructor() {
        super(...arguments);
        this.categories = [];
        this.railWidth = 240;
        this.variant = 'primary';
        this.size = 'medium';
        this.special = false;
    }
    /** The currently active category id (explicit selection or first category). */
    get activeId() {
        if (this.selectedId && this.categories.some((c) => c.id === this.selectedId)) {
            return this.selectedId;
        }
        return this.categories[0]?.id;
    }
    _select(category) {
        if (category.disabled || category.id === this.activeId)
            return;
        this.selectedId = category.id;
        this.dispatchEvent(new CustomEvent('category-change', {
            detail: { id: category.id },
            bubbles: true,
            composed: true,
        }));
    }
    render() {
        const active = this.activeId;
        const activeCategory = this.categories.find((c) => c.id === active);
        return html `
      <nav class="rail" style="width:${this.railWidth}px" aria-label=${msg('Categories', { id: 'navRail.ariaLabel', desc: 'Accessible label for the navigation rail' })}>
        ${this.categories.map((category) => {
            const isActive = category.id === active;
            return html `
            <simple-button
              fluid
              .label=${category.label}
              .size=${this.size}
              .variant=${isActive ? this.variant : 'secondary'}
              ?special=${isActive && this.special}
              ?disabled=${category.disabled ?? false}
              @button-click=${() => this._select(category)}
            >
              ${category.icon
                ? html `<basic-icon .name=${category.icon} size="16"></basic-icon>`
                : nothing}
            </simple-button>
          `;
        })}
      </nav>

      <section class="detail">
        ${activeCategory
            ? html `<h2 class="detail__title">${activeCategory.label}</h2>`
            : nothing}
        <div class="detail__body">
          ${this.categories.map((category) => html `
              <div class="pane" ?hidden=${category.id !== active}>
                <slot name=${category.id}></slot>
              </div>
            `)}
        </div>
      </section>
    `;
    }
};
NavRailLayout.styles = css `
    :host {
      display: flex;
      width: 100%;
      height: 100%;
      min-height: 300px;
      overflow: hidden;
      font-family: inherit;
      box-sizing: border-box;
    }

    .rail {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 12px;
      box-sizing: border-box;
      overflow-y: auto;
    }

    .detail {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-width: 0;
      overflow: hidden;
    }

    .detail__title {
      flex: 0 0 auto;
      margin: 0;
      padding: 16px;
      font-size: 18px;
      font-weight: 700;
      color: #111827;
    }

    .detail__body {
      flex: 1 1 auto;
      overflow: auto;
      padding: 16px;
      box-sizing: border-box;
    }

    .pane[hidden] {
      display: none;
    }
  `;
__decorate([
    property({ type: Array })
], NavRailLayout.prototype, "categories", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'selected-id' })
], NavRailLayout.prototype, "selectedId", void 0);
__decorate([
    property({ type: Number, attribute: 'rail-width' })
], NavRailLayout.prototype, "railWidth", void 0);
__decorate([
    property({ type: String })
], NavRailLayout.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], NavRailLayout.prototype, "size", void 0);
__decorate([
    property({ type: Boolean })
], NavRailLayout.prototype, "special", void 0);
NavRailLayout = __decorate([
    localized(),
    customElement('nav-rail-layout')
], NavRailLayout);
export { NavRailLayout };
//# sourceMappingURL=NavRailLayout.js.map